import React, { useCallback, useEffect, useRef, useState } from 'react'
import Cropper from 'react-easy-crop'
import { CropArea, getCroppedLogoBlob } from '../../../../../utils/cropLogo'

type Props = {
    open: boolean
    onClose: () => void
    currentUserAvatar?: string | null
    onSaved: (userAvatar: string) => void
    onDeleted: () => void
    userGuid: string | null
}

const UserAvatarModal: React.FC<Props> = ({
    open,
    onClose,
    currentUserAvatar,
    onSaved,
    onDeleted,
    userGuid,
}) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    const [imageSrc, setImageSrc] = useState<string | null>(null)
    const [originalFilename, setOriginalFilename] =
        useState<string>('user_avatar.png')
    const [crop, setCrop] = useState({ x: 0, y: 0 })

    const MIN_ZOOM = 0.2
    const MAX_ZOOM = 5

    const [zoom, setZoom] = useState(1)

    const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(
        null
    )

    const [isDragOver, setIsDragOver] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    useEffect(() => {
        if (open) {
            setCrop({ x: 0, y: 0 })
            setZoom(1)

            if (currentUserAvatar) {
                setImageSrc(
                    `${process.env.REACT_APP_API_URL}${currentUserAvatar}?t=${Date.now()}`
                )
            } else {
                setImageSrc(null)
            }
        }
    }, [open, currentUserAvatar])

    const onCropComplete = useCallback(
        (_croppedArea: CropArea, croppedPixels: CropArea) => {
            setCroppedAreaPixels(croppedPixels)
        },
        []
    )

    const loadFile = (file: File) => {
        if (!['image/png', 'image/jpeg'].includes(file.type)) {
            alert('Допустимы только PNG и JPG')
            return
        }

        setOriginalFilename(file.name)

        const reader = new FileReader()
        reader.onload = () => {
            setImageSrc(reader.result as string)
            setCrop({ x: 0, y: 0 })
            setZoom(1)
        }
        reader.readAsDataURL(file)
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            loadFile(file)
        }
    }

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragOver(false)

        const file = e.dataTransfer.files?.[0]
        if (file) {
            loadFile(file)
        }
    }

    const handleSave = async () => {
        if (!imageSrc || !croppedAreaPixels) {
            alert('Сначала выберите логотип')
            return
        }

        try {
            setIsSaving(true)

            // итоговый файл всегда png 145x145
            const blob = await getCroppedLogoBlob(
                imageSrc,
                croppedAreaPixels,
                145,
                'image/png'
            )

            const safeName = originalFilename.includes('.')
                ? `${originalFilename.split('.').slice(0, -1).join('.') || 'user_avatar'}.png`
                : 'user_avatar.png'

            const file = new File([blob], safeName, {
                type: 'image/png',
            })

            const formData = new FormData()
            formData.append('file', file)

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/backend/v1/user/user_avatar/${userGuid}`,
                {
                    method: 'POST',
                    credentials: 'include',
                    body: formData,
                }
            )

            if (!response.ok) {
                throw new Error('Ошибка при загрузке логотипа')
            }

            const data = await response.json()
            onSaved(data.user_avatar)
            onClose()
        } catch (error) {
            console.error(error)
            alert('Не удалось сохранить логотип')
        } finally {
            setIsSaving(false)
        }
    }

    const handleDelete = async () => {
        try {
            setIsDeleting(true)

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/backend/v1/user/user_avatar/${userGuid}`,
                {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )

            if (!response.ok) {
                throw new Error('Ошибка при удалении логотипа')
            }

            onDeleted()
            onClose()
        } catch (error) {
            console.error(error)
            alert('Не удалось удалить логотип')
        } finally {
            setIsDeleting(false)
        }
    }

    if (!open) return null

    return (
        <div className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-50 flex justify-center items-center z-50">
            <div
                className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-fit h-fit"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="text-[#181818] dark:text-[#FFFFFF] text-xl mb-4">
                    Загрузка аватара
                </h2>

                {!imageSrc ? (
                    <div
                        className={`w-[400px] h-[400px] mx-auto rounded-md border bg-[#e4e6e8] dark:bg-[#1D1C1E] ${
                            isDragOver
                                ? 'border-[#1485ff]'
                                : 'border-dashed border-[#3a3a3a]'
                        } flex flex-col items-center justify-center`}
                        onDragOver={(e) => {
                            e.preventDefault()
                            setIsDragOver(true)
                        }}
                        onDragLeave={() => setIsDragOver(false)}
                        onDrop={handleDrop}
                    >
                        <div className="w-[145px] h-[145px] rounded-full border border-dashed border-spacing-1 border-[#8A8A8A] flex flex-col items-center justify-center">
                            <button
                                className="flex min-w-[120px] h-6 px-2 rounded-[10px] items-center justify-center text-center text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Выберите аватар
                            </button>
                            <div className="flex items-center justify-center text-[#6A6A6A] dark:text-[#8A8A8A] text-[11px] text-center mt-3">
                                или перетащите сюда
                            </div>
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".png,.jpg,.jpeg"
                            className="hidden"
                            onChange={handleFileChange}
                        />
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-4">
                        <div className="relative w-[400px] h-[400px] rounded-md bg-[#ffffff] dark:bg-[#1D1C1E] border border-dashed border-[#1485ff] overflow-hidden">
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                cropSize={{ width: 145, height: 145 }}
                                cropShape="round"
                                showGrid={false}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                                minZoom={MIN_ZOOM}
                                maxZoom={MAX_ZOOM}
                                objectFit="contain"
                            />
                        </div>

                        <div className="w-[400px] flex justify-center">
                            <button
                                className="px-4 h-6 rounded-[10px] text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Выбрать другой аватар
                            </button>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".png,.jpg,.jpeg"
                                className="hidden"
                                onChange={handleFileChange}
                            />
                        </div>

                        <div className="w-[400px]">
                            <label className="text-[#6A6A6A] dark:text-[#8A8A8A] text-[12.8px]">
                                Масштаб
                            </label>
                            <input
                                type="range"
                                min={MIN_ZOOM}
                                max={MAX_ZOOM}
                                step={0.05}
                                value={zoom}
                                onChange={(e) =>
                                    setZoom(Number(e.target.value))
                                }
                                className="w-full"
                            />
                        </div>
                    </div>
                )}

                <div className="flex flex-row mt-6 justify-end gap-3">
                    {currentUserAvatar && (
                        <button
                            className="px-4 min-w-[120px] h-6 rounded-[10px] text-xs shadow-md shadow-[#18181830] text-[#181818] bg-[#ed9cae] hover:bg-[#ffa7ba] dark:bg-[#701F1F] dark:hover:bg-[#9e2c2c] dark:text-[#FFFFFF]"
                            onClick={handleDelete}
                            disabled={isDeleting}
                        >
                            {isDeleting ? 'Удаление...' : 'Удалить'}
                        </button>
                    )}

                    <button
                        className="px-4 min-w-[120px] h-6 rounded-[10px] text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                        onClick={onClose}
                    >
                        Закрыть
                    </button>

                    <button
                        className={`px-4 min-w-[120px] h-6 rounded-[10px] text-xs ${!imageSrc || isSaving ? 'text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#EDEDED] dark:bg-[#494849]' : 'shadow-md shadow-[#18181830] text-xs text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'} `}
                        onClick={handleSave}
                        disabled={!imageSrc || isSaving}
                    >
                        {isSaving ? 'Сохранение...' : 'Сохранить'}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default UserAvatarModal
